/**
 * AdminOpsProvider · v2.7.65+E120
 * ═══════════════════════════════════════════════════════════════════
 * Top-level operational shell for every admin page. Ensures that:
 *
 *  1. A single `/api/ws/dispatch` subscription stays open across route
 *     transitions (chat → wiki → dashboard → helpdesk), so the 5
 *     critical operational triggers never go silent when the dispatcher
 *     is off the dashboard.
 *  2. The global `useAudioAlerts` engine is armed on every admin page
 *     (previously mounted only on AdminDashboardPage → audio went dark
 *     the moment ops navigated away).
 *  3. The autoplay-policy primer runs once so subsequent alert sounds
 *     play immediately without a click.
 *
 * The 5 critical triggers (re-broadcast as window CustomEvents so the
 * useAudioAlerts hook, live-map, and inbox tiles can react uniformly):
 *
 *   • ONLINE BOOKINGS       → `rtg:public_booking` (LOOP alarm)
 *   • DRIVER INITIATED RIDE → `rtg:trip_initiated`
 *   • DRIVER FINISHED RIDE  → `rtg:trip_completed`
 *   • CUSTOMER CHAT STREAM  → `rtg:chat_message` (+ `chat_message_payload` alias)
 *   • TICKETING PIPELINE    → `rtg:ticket_pipeline`
 */
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useAudioAlerts from "@/hooks/useAudioAlerts";
import AudioActivationOverlay from "@/components/admin/AudioActivationOverlay";

const BACKEND_HTTP = process.env.REACT_APP_BACKEND_URL || "";
const BACKEND_WS   = BACKEND_HTTP.replace(/^http/, "ws");
const RECONNECT_MIN_MS = 1500;
const RECONNECT_MAX_MS = 30000;

/** Fire a window CustomEvent iff we're on a browser. */
function fire(name, detail) {
  if (typeof window === "undefined") return;
  try { window.dispatchEvent(new CustomEvent(name, { detail })); } catch { /* noop */ }
}

/**
 * Persistent lightweight dispatch-channel WS. Runs independently of the
 * dashboard's useFleetStream — this listener only re-broadcasts events
 * for the audio engine and inbox tiles so we don't fight over state.
 * Reconnects with exponential backoff.
 */
function useAdminAlertStream() {
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || !BACKEND_WS) return undefined;
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      const token = localStorage.getItem("rtg_admin_token");
      if (!token) {
        // Not logged in yet — retry once a token appears.
        timerRef.current = setTimeout(open, 2000);
        return;
      }
      const url = `${BACKEND_WS}/api/ws/dispatch?token=${encodeURIComponent(token)}`;
      let ws;
      try { ws = new WebSocket(url); }
      catch { timerRef.current = setTimeout(open, RECONNECT_MIN_MS); return; }
      wsRef.current = ws;

      ws.onopen = () => { retryRef.current = 0; };

      ws.onmessage = (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (!m || typeof m !== "object") return;

        // ── 5 CRITICAL TRIGGERS ─────────────────────────────────────
        switch (m.type) {
          case "public_booking":
            fire("rtg:public_booking", m);
            break;
          case "trip_initiated":
            fire("rtg:trip_initiated", m);
            break;
          case "trip_completed":
            fire("rtg:trip_completed", m);
            break;
          // v2.7.65+E146 · Phase 3.5 · New Trip upfront save toast on the
          // Dispatch board — dispatchers see the fare land in real time
          // instead of waiting for the next poll.
          case "new_trip_created":
            fire("rtg:new_trip_created", m);
            try {
              const rider = m.customer_name || m.driver_name || "Rider";
              const dest  = m.destination || "Trip";
              const total = typeof m.total === "number" ? ` · $${m.total.toFixed(2)}` : "";
              if (typeof window !== "undefined" && window.sonner_toast) {
                window.sonner_toast(`New Trip · ${rider} → ${dest}${total}`, {
                  description: m.driver_name ? `Driver ${m.driver_name}` : undefined,
                });
              } else {
                // Fallback — import at runtime so we don't add a hard dep
                // to this provider file.
                import("sonner").then(({ toast }) => {
                  toast(`New Trip · ${rider} → ${dest}${total}`, {
                    description: m.driver_name ? `Driver ${m.driver_name}` : undefined,
                  });
                }).catch(() => { /* no-op */ });
              }
            } catch { /* toast is best-effort */ }
            break;
          case "panic_alert":
            fire("rtg:panic_alert", m);
            break;
          case "chat.message":
            fire("rtg:chat_message", m);
            // Legacy-compat alias requested for external listeners.
            fire("chat_message_payload", m);
            break;
          case "chat.escalated":
            // A brand-new customer just hit "Talk to human" — treat as a
            // fresh chat stream ping so ops hear it wherever they are.
            fire("rtg:chat_message", m);
            fire("chat_message_payload", m);
            break;
          case "ticket.pipeline":
            fire("rtg:ticket_pipeline", m);
            break;
          // v2.8+E337 · Dialpad inbound call — re-broadcast so the open
          // Dispatcher booking form can auto-populate the caller in real time.
          case "dialpad.call":
            fire("rtg:dialpad_call", m);
            break;
          case "wiki.article_changed":
            fire("rtg:wiki_article_changed", m);
            break;
          default:
            break;
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (cancelled) return;
        retryRef.current = Math.min(retryRef.current + 1, 10);
        const delay = Math.min(
          RECONNECT_MIN_MS * Math.pow(2, retryRef.current - 1),
          RECONNECT_MAX_MS,
        );
        timerRef.current = setTimeout(open, delay);
      };

      ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
    };

    open();
    return () => {
      cancelled = true;
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, []);
}

export default function AdminOpsProvider({ children }) {
  useAdminAlertStream();
  useAudioAlerts();
  useTicketPipelineToast();

  return (
    <>
      <AudioActivationOverlay />
      {children}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// Ticket-pipeline toast · v2.7.65+E121
// Every ticket lifecycle event fires a floating sonner toast so ops see
// WHO changed WHAT without leaving their current admin page.
// ────────────────────────────────────────────────────────────────────
const TICKET_EVENT_COPY = {
  created:      { label: "Ticket opened",   tone: "info"    },
  claimed:      { label: "Ticket claimed",  tone: "success" },
  assigned:     { label: "Ticket routed",   tone: "info"    },
  transitioned: { label: "Ticket updated",  tone: "info"    },
  commented:    { label: "New comment",     tone: "info"    },
};

function useTicketPipelineToast() {
  const seenRef = useRef(new Set());
  const navigate = useNavigate();
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onPipeline = (e) => {
      const p = e?.detail || {};
      // Dedup — the same ticket_id + event combo shouldn't toast twice
      // per second (WS retries, tab focus refires, etc).
      const key = `${p.ticket_id}::${p.event}::${p.status || ""}::${Math.floor(Date.now() / 1500)}`;
      if (seenRef.current.has(key)) return;
      seenRef.current.add(key);
      // Cap dedup memory
      if (seenRef.current.size > 500) seenRef.current = new Set();

      const meta = TICKET_EVENT_COPY[p.event] || { label: "Ticket event", tone: "info" };
      const num = p.ticket_no ? `#${p.ticket_no}` : "";
      const subject = p.subject ? ` · ${p.subject.slice(0, 40)}${p.subject.length > 40 ? "…" : ""}` : "";
      const status = p.status ? ` → ${p.status.replace(/_/g, " ")}` : "";
      const body = `${num}${subject}${status}`.trim() || "Ticket activity";

      // v2.7.65+E124 · Clickable toast — jumps straight to the ticket
      // detail page in the Helpdesk (deep-link supported via ?ticket=).
      const openTicket = () => {
        if (!p.ticket_id) return;
        try { navigate(`/admin/helpdesk?ticket=${encodeURIComponent(p.ticket_id)}`); }
        catch { /* SSR / no router */ }
      };
      const toastOpts = {
        description: body,
        duration: 6000,
        action: p.ticket_id
          ? { label: "Open", onClick: openTicket }
          : undefined,
        onClick: openTicket,
        className: p.ticket_id ? "cursor-pointer" : undefined,
      };
      if (meta.tone === "success") toast.success(meta.label, toastOpts);
      else                          toast(meta.label,        toastOpts);
    };
    window.addEventListener("rtg:ticket_pipeline", onPipeline);
    return () => window.removeEventListener("rtg:ticket_pipeline", onPipeline);
  }, [navigate]);
}
